/**
 * @fileoverview Runtime configuration.
 *
 * Each host resolves in this order: RUNTIME CONFIGURATION written by the
 * container at start (`/config.js`, read through @cloistr/collab-common's
 * shared reader), then Vite's `import.meta.env` (VITE_ prefixed), then the
 * process environment, then the production default. Nothing here is a secret:
 * this is a public read path and every endpoint below is one a stranger can
 * reach anyway.
 *
 * RUNTIME FIRST IS WHAT LETS ONE IMAGE SERVE PRODUCTION AND STAGING. The
 * bundle is built once and run in both, so a value frozen into it at build time
 * can only ever name one of them. The production defaults stay, which is what
 * makes an image given no configuration -- and the CLI, which never has any --
 * behave exactly as before.
 *
 * IT ALSO READS `process.env`, AND THAT IS WHAT MAKES THE SERVICE LAYER USABLE
 * OUTSIDE A BROWSER. `import.meta.env` is a Vite build-time substitution and
 * does not exist under plain Node, so every module that transitively imports
 * this one used to throw on the first property access the moment it was run
 * headlessly. The publishing CLI runs exactly these modules rather than a
 * second implementation of them, so the alternative was a parallel config, and
 * a parallel config is how two copies of "which relay do we publish to" start
 * disagreeing.
 *
 * ORDER IS `import.meta.env` FIRST. In the browser that is the only populated
 * one, and in a Vite build `process` may not exist at all, so the lookup is
 * guarded rather than assumed.
 */

import { getRuntimeConfig, getServiceConfig } from '@cloistr/collab-common/config';

/**
 * A runtime value, or null when the container gave none.
 *
 * Fields the shared contract defines go through `getServiceConfig`, which
 * reports a malformed value and then USES it rather than falling back: a typo
 * in a staging deployment that silently reached production is the failure the
 * whole split exists to prevent. `nip05Domain` is Pages' own addition to the
 * contract, because no shared field names the domain readable links resolve
 * against.
 */
function fromRuntime(
  field: 'relayUrl' | 'blossomUrl' | 'appUrl' | 'environment' | 'nip05Domain' | 'refCardUrl' | 'refThreadUrl'
): string | null {
  const value = (getRuntimeConfig() as Record<string, unknown>)[field];
  if (typeof value !== 'string' || value === '') return null;
  if (field === 'relayUrl' || field === 'blossomUrl') return getServiceConfig()[field];
  return value;
}

/** A comma-separated list, trimmed, with empties dropped. */
function list(value: string): string[] {
  return value
    .split(',')
    .map((v) => v.trim())
    .filter(Boolean);
}

/** One setting, from the browser's env, then the process's, then the default. */
function setting(name: string, fallback: string): string {
  const vite = (import.meta as { env?: Record<string, string | undefined> }).env;
  const fromVite = vite?.[name];
  if (typeof fromVite === 'string' && fromVite !== '') return fromVite;

  // `typeof` rather than a truthiness check: referencing an undeclared global
  // throws, and in a browser bundle `process` genuinely is undeclared.
  const fromNode =
    typeof process !== 'undefined' ? (process.env as Record<string, string | undefined>)[name] : undefined;
  if (typeof fromNode === 'string' && fromNode !== '') return fromNode;

  return fallback;
}

function hostOf(url: string | null): string | null {
  if (!url) return null;
  try {
    return new URL(url).host;
  } catch {
    return null;
  }
}

export const config = {
  /**
   * The relay we fall back to when an `naddr` carries no hints, or when its
   * hints do not answer.
   *
   * It is a FALLBACK and never a replacement. An `naddr` names a page on
   * whatever relay its author chose, and a renderer that only ever asks our
   * relay cannot open anyone else's page -- which is the property that makes
   * the canonical URL worth having.
   */
  fallbackRelays: list(fromRuntime('relayUrl') ?? setting('VITE_RELAYS', 'wss://relay.cloistr.xyz')),

  /**
   * Blossom servers tried after the page's own hints and the author's BUD-03
   * list. Content addressing makes an untrusted mirror safe to read from --
   * the bytes are verified against the hash before anything renders -- so this
   * list costs nothing but a request.
   */
  fallbackBlossomServers: list(
    fromRuntime('blossomUrl') ?? setting('VITE_BLOSSOM_SERVERS', 'https://files.cloistr.xyz')
  ),

  /**
   * The ONE domain a bare name in a readable URL resolves against, via NIP-05.
   *
   * One domain and not a pattern. `pages.cloistr.xyz/<author>/<slug>` would
   * otherwise become a proxy for anybody's `.well-known`: a link on our host
   * would send the reader's browser to a third party the link author chose, and
   * present whatever came back as one of our pages. Someone else's page is
   * reached by its `naddr`, which needs no name service at all.
   *
   * Root rather than `id.`: the root domain routes to cloistr-me, which serves
   * NIP-05 (architecture/dns-cloistr.md). Verified live 2026-09-15 -- it
   * answers, and it has no names registered yet.
   */
  nip05Domain: fromRuntime('nip05Domain') ?? setting('VITE_NIP05_DOMAIN', 'cloistr.xyz'),

  /** This app's own host, for copy that names it. Never used to build a link. */
  appHost: hostOf(fromRuntime('appUrl')) ?? 'pages.cloistr.xyz',

  /**
   * Link templates for the `:ref` directive's board cards and threads, each an
   * `https://` URL with `{id}` in it. Cards link to the tasks board's per-card
   * page (format confirmed by cloistr-tasks via cloistr-orchestrator,
   * 2026-10-15). Threads stay EMPTY, because Space has no per-thread link yet,
   * so a thread renders as a labelled non-link. Setting one later turns every
   * published reference into a link without touching a page.
   */
  refUrls: {
    card: fromRuntime('refCardUrl') ?? setting('VITE_REF_CARD_URL', 'https://tasks.cloistr.xyz/card/{id}'),
    thread: fromRuntime('refThreadUrl') ?? setting('VITE_REF_THREAD_URL', ''),
  },

  /** Which environment the container says this is. Unset is production. */
  environment: fromRuntime('environment') ?? 'production',

  /** How long a relay query waits before giving up, in milliseconds. */
  relayTimeoutMs: Number(setting('VITE_RELAY_TIMEOUT_MS', '6000')),

  /**
   * How long one relay may take to answer a publish, AUTH round trip included.
   * Generous, because AUTH through a remote signer can wait on a phone.
   */
  publishTimeoutMs: Number(setting('VITE_PUBLISH_TIMEOUT_MS', '15000')),

  /** How long a single blob fetch waits before moving to the next server. */
  blobTimeoutMs: Number(setting('VITE_BLOB_TIMEOUT_MS', '8000')),
} as const;
